import { IntegrationScope, OAUTH_INTEGRATION_LABELS, OauthIntegrationKind } from '@vivport/backend/types';
import googleLogo from './logos/google.svg';
import mcpLogo from './logos/mcp.svg';
import microsoftLogo from './logos/microsoft.svg';
import serverLogo from './logos/server.svg';
import xeroLogo from './logos/xero.svg';

export type IntegrationKind = OauthIntegrationKind | 'relay' | 'mcp';

export type { IntegrationScope };

export type IntegrationKindDefinition = {
  kind: IntegrationKind;
  label: string;
  vendor: string;
  category: 'Mail & calendar' | 'Files' | 'Finance' | 'Tools';
  scope: IntegrationScope;
  blurb: string;
  logo: string;
  maxPerScope: 1 | null;
  addMode: 'oauth' | 'dialog';
};

export const INTEGRATION_KINDS: readonly IntegrationKindDefinition[] = [
  {
    kind: 'google-account',
    label: OAUTH_INTEGRATION_LABELS['google-account'],
    vendor: 'Google',
    category: 'Mail & calendar',
    scope: 'member',
    blurb: 'Import Gmail and Google Calendar, and compose or send from your Gmail address.',
    logo: googleLogo,
    maxPerScope: 1,
    addMode: 'oauth',
  },
  {
    kind: 'microsoft-account',
    label: OAUTH_INTEGRATION_LABELS['microsoft-account'],
    vendor: 'Microsoft',
    category: 'Mail & calendar',
    scope: 'member',
    blurb: 'Import Outlook mail and calendar, and compose or send from your Outlook address.',
    logo: microsoftLogo,
    maxPerScope: 1,
    addMode: 'oauth',
  },
  {
    kind: 'google-drive',
    label: OAUTH_INTEGRATION_LABELS['google-drive'],
    vendor: 'Google',
    category: 'Files',
    scope: 'hub',
    blurb: "Reference this Hub's shared drives and a connected account's My Drive, and publish this Hub's projects back as folders.",
    logo: googleLogo,
    maxPerScope: null,
    addMode: 'oauth',
  },
  {
    kind: 'microsoft-tenant',
    label: OAUTH_INTEGRATION_LABELS['microsoft-tenant'],
    vendor: 'Microsoft',
    category: 'Files',
    scope: 'hub',
    blurb: "Reference this Hub's SharePoint document libraries once a tenant admin grants access, and publish this Hub's projects back as folders.",
    logo: microsoftLogo,
    maxPerScope: null,
    addMode: 'oauth',
  },
  {
    kind: 'relay',
    label: 'On-premises folder',
    vendor: 'On-premises',
    category: 'Files',
    scope: 'hub',
    blurb: 'Install a small relay on your server so this Hub can reference folders and shares without moving them.',
    logo: serverLogo,
    maxPerScope: null,
    addMode: 'dialog',
  },
  {
    kind: 'xero',
    label: OAUTH_INTEGRATION_LABELS['xero'],
    vendor: 'Xero',
    category: 'Finance',
    scope: 'hub',
    blurb: 'Connect this Hub to a Xero organisation so Agents and Neo can work with contacts and invoices.',
    logo: xeroLogo,
    maxPerScope: 1,
    addMode: 'oauth',
  },
  {
    kind: 'mcp',
    label: 'Tool server (MCP)',
    vendor: 'MCP',
    category: 'Tools',
    scope: 'hub',
    blurb: "Connect an external MCP tool server this Hub's Neo can use in chat.",
    logo: mcpLogo,
    maxPerScope: null,
    addMode: 'dialog',
  },
];

export function getIntegrationKind(kind: IntegrationKind): IntegrationKindDefinition {
  const definition = INTEGRATION_KINDS.find((entry) => entry.kind === kind);
  if (!definition) {
    throw new Error(`Unknown integration kind: ${kind}`);
  }
  return definition;
}

export function isIntegrationKind(value: string | undefined): value is IntegrationKind {
  return INTEGRATION_KINDS.some((entry) => entry.kind === value);
}

export function integrationHref(kind: IntegrationKind, id: number): string {
  return `/settings/integrations/${kind}/${id}`;
}
